/**
 * Server-side search (wff_search, migration 00539): requests, documents and
 * forms, ranked by Postgres full-text search, and only what the signed-in
 * person may read (record access and CUI). Controlled results carry the
 * cui flag and no value snippet.
 */
import { supabase } from '@/lib/supabase'

export type SearchType = 'request' | 'document' | 'form'

export interface SearchFilters {
  types?: SearchType[]
  formFamilies?: string[]
  siteId?: string | null
  status?: string | null
  from?: string | null
  to?: string | null
}

export interface SearchHit {
  type: SearchType
  id: string
  title: string | null
  number?: string | null
  formId?: string
  formName?: string | null
  family?: string
  status?: string | null
  siteId?: string | null
  submitter?: string | null
  createdAt?: string
  updatedAt?: string
  libraryId?: string
  libraryName?: string | null
  documentType?: string | null
  revision?: string | null
  snippet?: string | null
  cui?: boolean
  rank?: number
}

export interface SearchResult { results: SearchHit[]; counts: Record<SearchType, number> }

export const SEARCH_TYPE_LABEL: Record<SearchType, string> = { request: 'Requests', document: 'Documents', form: 'Forms' }

/** Fewer than two letters or digits finds nothing useful. */
export function searchable(query: string): boolean {
  return query.replace(/[^\p{L}\p{N}]+/gu, '').length >= 2
}

export async function searchRecords(query: string, filters: SearchFilters = {}, limit = 20): Promise<SearchResult> {
  const clean: Record<string, unknown> = {}
  if (filters.types?.length) clean.types = filters.types
  if (filters.formFamilies?.length) clean.formFamilies = filters.formFamilies
  if (filters.siteId) clean.siteId = filters.siteId
  if (filters.status) clean.status = filters.status
  if (filters.from) clean.from = filters.from
  if (filters.to) clean.to = filters.to
  const { data, error } = await supabase.rpc('wff_search', { p_query: query, p_filters: clean, p_limit: limit })
  if (error) {
    if (/wff_search|schema cache|does not exist/i.test(error.message)) throw new Error('Search is not installed in this environment yet. Ask an administrator to apply database migration 00539.')
    throw new Error(error.message)
  }
  const r = (data ?? {}) as Partial<SearchResult>
  return {
    results: Array.isArray(r.results) ? r.results : [],
    counts: { request: Number(r.counts?.request ?? 0), document: Number(r.counts?.document ?? 0), form: Number(r.counts?.form ?? 0) },
  }
}

/** Where a hit opens. */
export function hitLink(hit: SearchHit): string {
  if (hit.type === 'request') return `/workflow-management/requests/${encodeURIComponent(hit.id)}/record`
  if (hit.type === 'document') return `/workflow-management/libraries/${encodeURIComponent(hit.libraryId ?? '')}`
  return `/workflow-management/forms/${encodeURIComponent(hit.id)}/submissions`
}

/** The second line of a hit: form, status and submitter, or library and revision. */
export function hitSubtitle(h: SearchHit): string {
  if (h.type === 'request') return [h.formName, h.status, h.submitter].filter(Boolean).join(' · ')
  if (h.type === 'document') return [h.libraryName, h.revision ? `Rev ${h.revision}` : null, h.status].filter(Boolean).join(' · ')
  return 'Form'
}

/** A snippet split into plain and highlighted parts ([[ ]] markers from the database). */
export function snippetParts(snippet: string | null | undefined): Array<{ text: string; hit: boolean }> {
  if (!snippet) return []
  const out: Array<{ text: string; hit: boolean }> = []
  const re = /\[\[([\s\S]*?)\]\]/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(snippet))) {
    if (m.index > last) out.push({ text: snippet.slice(last, m.index), hit: false })
    out.push({ text: m[1] ?? '', hit: true })
    last = m.index + m[0].length
  }
  if (last < snippet.length) out.push({ text: snippet.slice(last), hit: false })
  return out
}
